import {
    createSlice,
    createAsyncThunk,
    PayloadAction,
} from "@reduxjs/toolkit";
import {SearchIfuURL, TicketListUrl} from "../config/api";
import axios from "axios";
import dayjs from "dayjs";
import { getUserFriendlyError } from "@common/errorHandler";

export interface Comment {
    id: number;
    createdAt: string;
    attachments: any[];
    userName: string | null;
    type: string;
    body: string;
    public: boolean;
    autherId: number;
    searchIFU?: boolean
}

interface User {
    id: number;
    name: string;
    email: string | null;
    organizationId: number | null;
}

interface Metra {
    metraId: string,
    metraName: string,
    metraLocation: number,
    organizationId: number,
    hospitalId: string,
    hospitalName: string,
}

interface ChatState {
    chat: {
        messages: Comment[];
        users: Record<number, User>;
        loading: boolean;
        error: string | null;
        page: number;
        pageSize: number;
        totalCount: number;
        hasMore: boolean;
    };
    isPrivate: boolean;
    ticketCreate: {
        loading: boolean,
        id?: number,
        data: { [key: string]: string }
    },
    uploadStatus: "idle" | "loading" | "succeeded" | "failed";
    uploadError: string | null;
    attachments: File[];
    metraData: {
        loading: boolean,
        data: Metra[],
    },
    searchIFU: {
        loading: boolean,
        url?: string,
        error?: string
    }
    createTicketError: string | null;
}

const initialState: ChatState = {
    chat: {
        messages: [],
        users: {},
        page: 1,
        pageSize: 20,
        totalCount: 0,
        hasMore: true,
        loading: false,
        error: null,
    },
    isPrivate: false,
    ticketCreate: {
        loading: false,
        id: undefined,
        data: {}
    },
    uploadStatus: "idle",
    uploadError: null,
    attachments: [],
    metraData: {
        data: [],
        loading: false,
    },
    searchIFU: {
        loading: false,
        url: undefined,
        error: undefined
    },
    createTicketError:null,
};

interface FetchMessagesParams {
    ticketId: number;
    page?: number;
    pageSize?: number;
}

interface FetchMessageResponse {
    comments: Comment[];
    users: { [key: string]: any };
    totalCount: number;
    page: number;
}

export const fetchMessages = createAsyncThunk<
    FetchMessageResponse,
    FetchMessagesParams
>(
    "chat/fetchMessages",
    async (
        {ticketId, page = 1, pageSize = 20}: FetchMessagesParams,
        {rejectWithValue}
    ) => {
        try {
            let url = `${TicketListUrl}/${ticketId}/comments?page=${page}&pageSize=${pageSize}&sort_order=desc`;
            const response = await axios.get(url);
            if (response.data.success) {
                const users: Record<number, User> = {};
                response.data.data.users.forEach((user: User) => {
                    users[user.id] = user;
                });
                return {
                    comments: response.data.data.comments as Comment[],
                    users,
                    totalCount: response.data.data.count,
                    page,
                };
            } else {
                return rejectWithValue(
                    response.data.message || "Failed to fetch messages"
                );
            }
        } catch (error: any) {
            return rejectWithValue(getUserFriendlyError(error));
        }
    }
);

export const createTicket = createAsyncThunk(
    'chat/createTicket',
    async (_, {getState, rejectWithValue}: any) => {
        try{
        const {messages} = (await getState()).chat.chat
        const payload = {
            subject: messages.find((item: any) => item.id == 1)?.body.trim(),
            description: messages.find((item: any) => item.id == 5)?.body,
            metraInfo: messages.find((item: any) => item.id == 3)?.body,
        }
        const resp = await axios.post(TicketListUrl, payload)
        if (resp.status === 200)
            return resp.data.data
        return rejectWithValue("Something Went Wrong");
    }catch(error:any){
    return rejectWithValue(getUserFriendlyError(error));
    }
  }
)

export const uploadAttachments = createAsyncThunk(
    'chat/uploadAttachments',
    async ({ticketId, text, inputRef}: { ticketId: string, text: string, inputRef: any }, {
        rejectWithValue,
        getState
    }: any) => {
        try {
            const {attachments, isPrivate} = getState().chat
            const formData = new FormData();
            attachments.forEach((attachment: any) => {
                formData.append('files', attachment
                );
            });
            formData.append('isInternal', isPrivate)
            formData.append('message', text)
            let url = TicketListUrl + `/${ticketId}/comments/upload`;
            const response = await axios.put(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(getUserFriendlyError(error));
        }
    }
);

export const fetchMetraIds = createAsyncThunk(
    'metra/fetchMetraIds',
    async (hospitalId: { hospitalId: string }, {rejectWithValue}) => {
        try {
            const response = await axios.get(TicketListUrl + `/${String(hospitalId)}/devices`);
            if (response.data) {
                return response.data as Metra[];
            } else {
                return rejectWithValue(response.data.message || 'Failed to fetch metraIds');
            }
        } catch (error: any) {
            return rejectWithValue(getUserFriendlyError(error));
        }
    }
);

export const fetchIFUFile = createAsyncThunk(
    "ifuFile/fetchIFUFile",
    async (_, {rejectWithValue }) => {
        try {
            const response = await axios.get(SearchIfuURL,
                {responseType: 'blob'}
            );
            const blob = new Blob([response.data], {type: 'application/pdf'});
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            return blobUrl;
        } catch (error: any) {
            return rejectWithValue(getUserFriendlyError(error));
        }
    }
);

const chatSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        setAttachments: (state, action: PayloadAction<File[]>) => {
            state.attachments = action.payload;
        },
        addMessageToTop: (state, action: PayloadAction<Comment>) => {
            if ((state.chat.messages.length + 1) % 20 === 1 && state.chat.messages.length > 1) {
                state.chat.messages.pop()
            }
            state.chat.messages.unshift(action.payload);
            state.chat.totalCount += 1;
        },
        resetTicketCreateId: (state) => {
            state.ticketCreate.id = undefined
        },
        clearMessages: (state) => {
            state.chat = initialState.chat
        },
        clearUploadStatus: (state) => {
            state.uploadStatus = "idle";
            state.uploadError = "";
        },
        clearTicketCreatedError:(state)=>{
            state.createTicketError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMessages.pending, (state, action: any) => {
                state.chat.loading = true;
                state.chat.error = null;
                if (action.meta.arg.page === 1) state.chat.messages = [];
            })
            .addCase(fetchMessages.fulfilled, (state, action) => {
                state.chat.loading = false;
                state.chat.error = null;
                if (action.payload.page === 1) {
                    state.chat.messages = action.payload.comments;
                    state.chat.users = action.payload.users;
                } else {
                    state.chat.messages = [
                        ...state.chat.messages,
                        ...action.payload.comments,
                    ];
                    state.chat.users = {...state.chat.users, ...action.payload.users};
                }
                state.chat.totalCount = action.payload.totalCount;
                state.chat.page = action.payload.page + 1;
                state.chat.hasMore = state.chat.messages.length < state.chat.totalCount;
            })
            .addCase(fetchMessages.rejected, (state, action) => {
                state.chat.loading = false;
                state.chat.error = action.payload as string;
                state.chat.messages = [];
            })
            .addCase(uploadAttachments.pending, (state) => {
                state.uploadStatus = "loading";
                state.uploadError = null;
            })
            .addCase(uploadAttachments.fulfilled, (state) => {
                state.uploadStatus = "succeeded";
                state.attachments = [];
            })
            .addCase(uploadAttachments.rejected, (state, action) => {
                state.uploadStatus = "failed";
                state.uploadError = action.payload as string;
            }).addCase(createTicket.pending, (state) => {
            state.ticketCreate.loading = true;
            state.createTicketError = null;
        })
            .addCase(createTicket.fulfilled, (state, action) => {
                state.ticketCreate.loading = false;
                state.ticketCreate.id = action.payload.ticket.id
                state.ticketCreate.data = action.payload.ticket
                state.chat.messages.unshift({
                    body: 'base64;' + btoa('ticket_created'),
                    id: 6,
                    createdAt: dayjs().format(),
                    attachments: [],
                    userName: null,
                    type: 'comment',
                    public: true,
                    autherId: 1
                });

            })
            .addCase(createTicket.rejected, (state, action) => {
                state.ticketCreate.loading = false;
                state.createTicketError = action.payload as string;
            })
            .addCase(fetchMetraIds.pending, (state) => {
                state.metraData.loading = true;
            })
            .addCase(fetchMetraIds.fulfilled, (state, action: PayloadAction<Metra[]>) => {
                state.metraData.loading = false;
                state.metraData.data = action.payload;
            })
            .addCase(fetchMetraIds.rejected, (state) => {
                state.metraData.loading = false;
                state.metraData.data = [];
            })
            .addCase(fetchIFUFile.pending, (state) => {
                state.searchIFU.loading = true;
            })
            .addCase(fetchIFUFile.fulfilled, (state, action) => {
                state.searchIFU.loading = false;
                state.searchIFU.error = undefined;
                state.searchIFU.url = action.payload;
            })
            .addCase(fetchIFUFile.rejected, (state, action) => {
                state.searchIFU.loading = false;
                state.searchIFU.error = action.error.message;
                state.searchIFU.url = undefined;
            })
    },
});

export const {setAttachments, clearMessages, addMessageToTop, resetTicketCreateId, clearUploadStatus, clearTicketCreatedError} =
    chatSlice.actions;
export default chatSlice.reducer;
